import { zodResolver } from '@hookform/resolvers/zod';
import { Property, User, UserGroupLink } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  LoadingIcon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AddressSearchInput } from '../../../components/AddressSearchInput';
import {
  BILLING_TERMS_OPTIONS,
  ONGOING_BILLING_TERMS,
  PRICING_MODEL_OPTIONS,
  USA_STATES_OPTIONS,
} from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { useGroupsQuery } from '../../../hooks/react-query/useGroupsQuery';
import { usePropertyCreateMutation } from '../../../hooks/react-query/usePropertiesQuery';
import {
  useUserGroupLinkCreateMutation,
  useUserGroupLinkDeleteMutation,
} from '../../../hooks/react-query/useUserGroupLinksQuery';
import {
  useBatchUserQuery,
  useUserCreateMutation,
  useUserUpdateMutation,
} from '../../../hooks/react-query/useUserQuery';
import {
  customerHourlyRate,
  PRICING_MODEL_TIME_AND_MATERIALS,
} from '../../../tools/customerBilling';
import { MapClientService, UserClientService } from '../../../tools/helpers';
import { customerFinancialAdminCheck } from '../../../tools/permissionChecks';
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import { getFieldMaskFromDirtyField } from '../../ComponentsLibrary/EmployeeDepartments/utils';
import { type Option } from '../../ComponentsLibrary/Field';
import { sendCustomerWelcomeEmail } from '../utils/customerWelcomeEmail';

const customerEditFormSchema = z
  .object({
    firstname: z.string().min(1, { message: 'First name is required' }),
    lastname: z.string().min(1, { message: 'Last name is required' }),
    businessname: z.string(),
    phone: z.string().refine((v) => !v || /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(v), {
      message: 'Enter a valid phone number',
    }),
    altphone: z.string().refine((v) => !v || /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(v), {
      message: 'Enter a valid phone number',
    }),
    cellphone: z.string().refine((v) => !v || /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(v), {
      message: 'Enter a valid phone number',
    }),
    email: z
      .string()
      .min(1, { message: 'Email is required' })
      .refine((v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), { message: 'Enter a valid email' }),
    login: z.string().min(1, { message: 'Login is required' }),
    altEmail: z.string(),
    receiveemail: z.boolean(),
    multiProperty: z.boolean(),
    address: z.string().min(1, { message: 'Address is required' }),
    city: z.string().min(1, { message: 'City is required' }),
    state: z.string().min(1, { message: 'State is required' }),
    zip: z
      .string()
      .min(1, { message: 'ZIP code is required' })
      .refine((v) => /^\d{5}(-\d{4})?$/.test(v), { message: 'Invalid ZIP code' }),
    billingTerms: z.string(),
    discount: z
      .string()
      .min(1, { message: 'Required' })
      .refine((v) => Number(v) >= 0, { message: 'Cannot be negative' }),
    rebate: z
      .string()
      .min(1, { message: 'Required' })
      .refine((v) => Number(v) >= 0, { message: 'Cannot be negative' }),
    recommendedBy: z.string(),
    billingInstructions: z.string(),
    employeeDepartmentID: z.string(),
    customerHourlyRate: z.string(),
    managedBy: z.string(),
    notes: z.string(),
    intNotes: z.string(),
    billingSameAsProperty: z.boolean(),
    propertyAddress: z.string(),
    propertyCity: z.string(),
    propertyState: z.string(),
    propertyZip: z.string(),
    propertyOwnershipTypeId: z.string().min(1, { message: 'Property ownership is required' }),
    propertyStoreNumber: z.string(),
    propertyNotes: z.string(),
  })
  .superRefine((data, ctx) => {
    if (
      data.employeeDepartmentID === String(PRICING_MODEL_TIME_AND_MATERIALS) &&
      Number(data.customerHourlyRate) <= 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required for Time & Materials',
        path: ['customerHourlyRate'],
      });
    }
    if (data.billingSameAsProperty) return;
    if (!data.propertyAddress) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Address is required',
        path: ['propertyAddress'],
      });
    }
    if (!data.propertyCity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'City is required',
        path: ['propertyCity'],
      });
    }
    if (!data.propertyState) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'State is required',
        path: ['propertyState'],
      });
    }
    if (!/^\d{5}(-\d{4})?$/.test(data.propertyZip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid ZIP code',
        path: ['propertyZip'],
      });
    }
  });

export type CustomerEditFormValues = z.infer<typeof customerEditFormSchema>;

const PROPERTY_FIELDS = [
  'propertyAddress',
  'propertyCity',
  'propertyState',
  'propertyZip',
] as const satisfies readonly (keyof CustomerEditFormValues)[];

const toOption = (opt: string | Option) =>
  typeof opt === 'string' ? { label: opt, value: opt } : opt;

/** Covers the form while a create runs, since that fans out across several sequential requests. */
const SavingOverlay: FC<{ stage: string | null }> = ({ stage }) => {
  if (!stage) return null;
  return (
    <div
      className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 bg-white/75 backdrop-blur-[1px]"
      aria-busy="true"
      aria-live="polite"
    >
      <LoadingIcon className="h-8 w-8 text-gray-600" />
      <span className="text-sm font-medium text-gray-700">{stage}</span>
    </div>
  );
};

export interface CustomerEditFormProps {
  customer: User;
  groupLinks: UserGroupLink[];
  /** `create` provisions a new customer (and their first property); `edit` patches an existing one. */
  mode?: 'create' | 'edit';
  /** Called after a successful save. */
  onSave: (customer: User) => void;
  /** Called when the user clicks Cancel or dismisses the form. */
  onCancel: () => void;
  /**
   * Phones, create mode: the same fields, rules and save, one step at a time (the customer, then
   * the address, then billing and extras), with each step's required fields checked before Next
   * and the optional contact fields behind "More contact details".
   */
  guided?: boolean;
}

/** The guided steps and the fields each one checks before moving on. */
const GUIDED_STEPS: { title: string; fields: (keyof CustomerEditFormValues)[] }[] = [
  {
    title: 'Customer',
    fields: [
      'firstname',
      'lastname',
      'businessname',
      'phone',
      'altphone',
      'cellphone',
      'email',
      'login',
      'altEmail',
    ],
  },
  {
    title: 'Address',
    fields: [
      'address',
      'city',
      'state',
      'zip',
      'billingSameAsProperty',
      'propertyAddress',
      'propertyCity',
      'propertyState',
      'propertyZip',
      'propertyStoreNumber',
      'propertyOwnershipTypeId',
      'propertyNotes',
    ],
  },
  {
    title: 'Billing and notes',
    fields: [
      'billingTerms',
      'discount',
      'rebate',
      'recommendedBy',
      'billingInstructions',
      'employeeDepartmentID',
      'customerHourlyRate',
      'managedBy',
      'notes',
      'intNotes',
    ],
  },
];

const OPTIONAL_CONTACT_FIELDS: (keyof CustomerEditFormValues)[] = [
  'altphone',
  'cellphone',
  'login',
  'altEmail',
];

export const CustomerEditForm: FC<CustomerEditFormProps> = ({
  customer,
  groupLinks: initialGroupLinks,
  mode = 'edit',
  onSave,
  onCancel,
  guided = false,
}) => {
  const auth = useAuth();
  const canEditFinancials = useMemo(() => customerFinancialAdminCheck(auth.user), [auth.user]);
  const isCreate = mode === 'create';

  const userUpdateMutation = useUserUpdateMutation();
  const userCreateMutation = useUserCreateMutation();
  const propertyCreateMutation = usePropertyCreateMutation();
  const createGroupLinkMutation = useUserGroupLinkCreateMutation();
  const deleteGroupLinkMutation = useUserGroupLinkDeleteMutation();

  const groupsQuery = useGroupsQuery();
  const groups = useMemo(() => groupsQuery.data?.results ?? [], [groupsQuery.data]);

  const employeesQuery = useBatchUserQuery({
    filters: { isEmployee: 1, isActive: 1, overrideLimit: true },
  });
  const employeeOptions = useMemo(() => {
    const results = employeesQuery.data?.results ?? [];
    return [
      { label: 'None', value: '0' },
      ...results
        .slice()
        .sort((a, b) => a.lastname.localeCompare(b.lastname))
        .map((e) => ({ label: `${e.lastname}, ${e.firstname}`, value: String(e.id) })),
    ];
  }, [employeesQuery.data]);

  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(
    () => new Set(initialGroupLinks.map((gl) => gl.groupId)),
  );
  const [initialGroupIds] = useState<Set<number>>(
    () => new Set(initialGroupLinks.map((gl) => gl.groupId)),
  );
  const [saveStage, setSaveStage] = useState<string | null>(null);

  const [step, setStep] = useState(0);
  const [isOptionalContactOpen, setIsOptionalContactOpen] = useState(false);
  const showStep = (index: number) => !guided || step === index;
  const showOptionalContact = !guided || isOptionalContactOpen;
  const sectionHeadingClass = guided
    ? 'text-muted-foreground text-xs font-semibold uppercase tracking-wide'
    : 'border-b pb-1 text-sm font-semibold text-gray-700';

  /** Checks this step's fields, then moves on, or stays and shows what's missing. */
  const goToNextStep = async () => {
    const fields = GUIDED_STEPS[step].fields;
    if (await form.trigger(fields)) {
      setStep(step + 1);
      window.scrollTo(0, 0);
      return;
    }
    const first = fields.find((field) => form.getFieldState(field).invalid);
    if (first) goToField(first);
  };

  /** Shows the step holding a field with an error, opening "More contact details" if it's there. */
  const goToField = (field: keyof CustomerEditFormValues) => {
    const index = GUIDED_STEPS.findIndex((guidedStep) => guidedStep.fields.includes(field));
    if (index >= 0) setStep(index);
    if (OPTIONAL_CONTACT_FIELDS.includes(field)) setIsOptionalContactOpen(true);
  };

  const form = useForm<CustomerEditFormValues>({
    resolver: zodResolver(customerEditFormSchema),
    defaultValues: {
      firstname: customer.firstname || '',
      lastname: customer.lastname || '',
      businessname: customer.businessname || '',
      phone: customer.phone ? formatPhoneNumber(customer.phone) : '',
      altphone: customer.altphone ? formatPhoneNumber(customer.altphone) : '',
      cellphone: customer.cellphone ? formatPhoneNumber(customer.cellphone) : '',
      email: customer.email || '',
      login: customer.login || '',
      altEmail: customer.altEmail || '',
      receiveemail: Boolean(customer.receiveemail),
      multiProperty: Boolean(customer.multiProperty),
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip: customer.zip || '',
      billingTerms: customer.billingTerms || '',
      discount: String(customer.discount ?? 0),
      rebate: String(customer.rebate ?? 0),
      recommendedBy: customer.recommendedBy || '',
      billingInstructions: customer.billingInstructions || '',
      employeeDepartmentID: String(customer.employeeDepartmentID ?? 0),
      customerHourlyRate: String(customerHourlyRate(customer)),
      managedBy: String(customer.managedBy ?? 0),
      notes: customer.notes || '',
      intNotes: customer.intNotes || '',
      billingSameAsProperty: true,
      propertyAddress: '',
      propertyCity: '',
      propertyState: '',
      propertyZip: '',
      propertyOwnershipTypeId: '1',
      propertyStoreNumber: '',
      propertyNotes: '',
    },
  });

  const billingSameAsProperty = form.watch('billingSameAsProperty');
  const [propertyAddress, propertyCity, propertyState, propertyZip] = form.watch(PROPERTY_FIELDS);

  // Property errors come from the object-level superRefine, so react-hook-form only clears the
  // one field it is revalidating. Re-run the whole group on any change, and drop stale errors
  // when the section is hidden.
  const { isSubmitted } = form.formState;
  useEffect(() => {
    if (!isCreate || !isSubmitted) return;
    if (billingSameAsProperty) {
      form.clearErrors(PROPERTY_FIELDS);
      return;
    }
    void form.trigger(PROPERTY_FIELDS);
  }, [
    isCreate,
    isSubmitted,
    billingSameAsProperty,
    form,
    propertyAddress,
    propertyCity,
    propertyState,
    propertyZip,
  ]);

  const employeeDeptId = form.watch('employeeDepartmentID');
  const isTimeAndMaterials = employeeDeptId === '2';

  // Billing instructions only apply to ongoing (invoiced) Net terms.
  const billingTermsValue = form.watch('billingTerms');
  const showBillingInstructions = ONGOING_BILLING_TERMS.includes(billingTermsValue);

  const toggleGroup = useCallback((groupId: number, checked: boolean) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(groupId);
      else next.delete(groupId);
      return next;
    });
  }, []);

  const saveGroupLinks = useCallback(
    async (userId: number) => {
      const creates: UserGroupLink[] = [];
      const deletes: UserGroupLink[] = [];
      for (const group of groups) {
        const wasSelected = initialGroupIds.has(group.id);
        const isSelected = selectedGroupIds.has(group.id);
        if (wasSelected && !isSelected) {
          const existing = initialGroupLinks.find((gl) => gl.groupId === group.id);
          if (existing) deletes.push(UserGroupLink.create({ id: existing.id }));
        } else if (!wasSelected && isSelected) {
          creates.push(UserGroupLink.create({ userId, groupId: group.id }));
        }
      }
      await Promise.all([
        ...creates.map((entry) => createGroupLinkMutation.mutateAsync(entry)),
        ...deletes.map((entry) => deleteGroupLinkMutation.mutateAsync(entry)),
      ]);
    },
    [
      groups,
      initialGroupIds,
      selectedGroupIds,
      initialGroupLinks,
      createGroupLinkMutation,
      deleteGroupLinkMutation,
    ],
  );

  const createProperty = useCallback(
    async (userId: number, data: CustomerEditFormValues) => {
      const address = data.billingSameAsProperty
        ? { address: data.address, city: data.city, state: data.state, zip: data.zip }
        : {
            address: data.propertyAddress,
            city: data.propertyCity,
            state: data.propertyState,
            zip: data.propertyZip,
          };
      const property = Property.create({
        userId,
        ...address,
        additionalAddressLine: data.propertyStoreNumber.trim(),
        propertyOwnershipTypeId: Number(data.propertyOwnershipTypeId),
        notes: data.propertyNotes,
      });
      try {
        const geo = await MapClientService.loadGeoLocationByAddress(
          `${address.address}, ${address.city}, ${address.state} ${address.zip}`,
        );
        if (geo) {
          property.geolocationLat = geo.geolocationLat;
          property.geolocationLng = geo.geolocationLng;
        }
      } catch {
        console.error('could not geolocate property address');
      }
      await propertyCreateMutation.mutateAsync(property);
    },
    [propertyCreateMutation],
  );

  const onCreate = async (data: CustomerEditFormValues) => {
    try {
      setSaveStage('Checking user account name…');
      const existing = await UserClientService.Get(
        User.create({ login: data.login, isActive: 1 }),
      ).catch(() => undefined);
      if (existing && existing.id !== 0) {
        form.setError('login', { message: 'Please use a unique user account name' });
        if (guided) goToField('login');
        return;
      }

      setSaveStage('Creating customer…');
      const created = await userCreateMutation.mutateAsync(
        User.create({
          firstname: data.firstname.trim(),
          lastname: data.lastname.trim(),
          businessname: data.businessname.trim(),
          phone: data.phone,
          altphone: data.altphone,
          cellphone: data.cellphone,
          email: data.email,
          login: data.login,
          altEmail: data.altEmail,
          pwd: data.phone,
          receiveemail: data.receiveemail ? 1 : 0,
          multiProperty: data.multiProperty ? 1 : 0,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          billingTerms: data.billingTerms,
          discount: Number(data.discount),
          rebate: Number(data.rebate),
          recommendedBy: data.recommendedBy,
          billingInstructions: data.billingInstructions,
          employeeDepartmentID: Number(data.employeeDepartmentID),
          customerHourlyRate: Number(data.customerHourlyRate),
          managedBy: Number(data.managedBy),
          notes: data.notes,
          intNotes: data.intNotes,
          isActive: 1,
          isEmployee: 0,
        }),
      );

      setSaveStage('Creating property…');
      await createProperty(created.id, data);

      setSaveStage('Saving groups…');
      await saveGroupLinks(created.id);

      setSaveStage('Sending welcome email…');
      try {
        await sendCustomerWelcomeEmail(created);
      } catch (err) {
        console.error(err);
        toast({
          variant: 'destructive',
          title: 'Customer created, but the welcome email could not be sent',
        });
      }

      toast({ variant: 'success', title: 'Customer created successfully' });
      onSave(created);
    } catch (err) {
      console.error(err);
      toast({ variant: 'destructive', title: 'Failed to create customer' });
    } finally {
      setSaveStage(null);
    }
  };

  const onSubmit = async (data: CustomerEditFormValues) => {
    if (isCreate) return await onCreate(data);

    const fieldMask = getFieldMaskFromDirtyField(dirtyFields);

    const groupsChanged =
      selectedGroupIds.size !== initialGroupIds.size ||
      [...selectedGroupIds].some((id) => !initialGroupIds.has(id));

    if (fieldMask.length === 0 && !groupsChanged) {
      toast({ variant: 'default', title: 'No changes to save' });
      onCancel();
      return;
    }

    try {
      setSaveStage('Saving changes…');
      if (fieldMask.length > 0) {
        const updated = User.create({
          id: customer.id,
          firstname: data.firstname,
          lastname: data.lastname,
          businessname: data.businessname,
          phone: data.phone,
          altphone: data.altphone,
          cellphone: data.cellphone,
          email: data.email,
          login: data.login,
          altEmail: data.altEmail,
          receiveemail: data.receiveemail ? 1 : 0,
          multiProperty: data.multiProperty ? 1 : 0,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          billingTerms: data.billingTerms,
          discount: Number(data.discount),
          recommendedBy: data.recommendedBy,
          billingInstructions: data.billingInstructions,
          employeeDepartmentID: Number(data.employeeDepartmentID),
          customerHourlyRate: Number(data.customerHourlyRate),
          managedBy: Number(data.managedBy),
          notes: data.notes,
          intNotes: data.intNotes,
          fieldMask,
        });
        updated.rebate = Number(data.rebate);
        await userUpdateMutation.mutateAsync(updated);
      }

      await saveGroupLinks(customer.id);

      toast({ variant: 'success', title: 'Customer updated successfully' });
      onSave(customer);
    } catch (err) {
      console.error(err);
      toast({ variant: 'destructive', title: 'Failed to update customer' });
    } finally {
      setSaveStage(null);
    }
  };

  const { dirtyFields } = form.formState;
  const isSubmitting =
    saveStage !== null ||
    userUpdateMutation.isPending ||
    userCreateMutation.isPending ||
    propertyCreateMutation.isPending;

  return (
    <div className="relative">
      <SavingOverlay stage={saveStage} />
      <Form {...form}>
        <form
          onSubmit={(event) => {
            // Enter on a step before the last means Next, not submit.
            if (guided && step < GUIDED_STEPS.length - 1) {
              event.preventDefault();
              void goToNextStep();
              return;
            }
            void form.handleSubmit(onSubmit, (errors) => {
              const first = Object.keys(errors)[0] as keyof CustomerEditFormValues | undefined;
              if (guided && first) goToField(first);
            })(event);
          }}
          className="space-y-6"
        >
          {guided && (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              Step {step + 1} of {GUIDED_STEPS.length}: {GUIDED_STEPS[step].title}
            </p>
          )}
          {/* Personal Details */}
          {showStep(0) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Personal Details</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="firstname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="businessname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>
          )}

          {/* Contact Details */}
          {showStep(0) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Contact Details</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Phone</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {showOptionalContact && (
                  <FormField
                    control={form.control}
                    name="altphone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alternate Phone</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {showOptionalContact && (
                  <FormField
                    control={form.control}
                    name="cellphone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cell Phone</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          onChange={(e) => {
                            field.onChange(e);
                            if (isCreate && !form.getFieldState('login').isDirty) {
                              form.setValue('login', e.target.value);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {showOptionalContact && (
                  <FormField
                    control={form.control}
                    name="login"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>User Account Name *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {showOptionalContact && (
                  <FormField
                    control={form.control}
                    name="altEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alternate Email(s)</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Separate with commas" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
              {showOptionalContact && (
                <div className="flex flex-wrap gap-6">
                  <FormField
                    control={form.control}
                    name="receiveemail"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(v) => field.onChange(Boolean(v))}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer font-normal">
                          Wishes to receive promotional emails
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="multiProperty"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(v) => field.onChange(Boolean(v))}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer font-normal">
                          Wishes to receive text messages
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              )}
              {guided && (
                <Button
                  type="button"
                  variant="ghost"
                  className="-ml-3 w-fit"
                  aria-expanded={isOptionalContactOpen}
                  onClick={() => setIsOptionalContactOpen((open) => !open)}
                >
                  {isOptionalContactOpen ? 'Fewer contact details' : 'More contact details'}
                </Button>
              )}
            </section>
          )}

          {/* Billing Address */}
          {showStep(1) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Billing Address</h3>
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Address *</FormLabel>
                    <FormControl>
                      {isCreate ? (
                        <AddressSearchInput
                          value={field.value}
                          onChange={field.onChange}
                          onSelect={(a) => {
                            form.setValue('address', a.address, { shouldValidate: true });
                            form.setValue('city', a.city, { shouldValidate: true });
                            form.setValue('state', a.state, { shouldValidate: true });
                            form.setValue('zip', a.zip, { shouldValidate: true });
                          }}
                          placeholder="Search address..."
                        />
                      ) : (
                        <Textarea {...field} rows={2} />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {USA_STATES_OPTIONS.map((opt) => {
                            const o = toOption(opt);
                            return (
                              <SelectItem key={String(o.value)} value={String(o.value)}>
                                {o.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP Code *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>
          )}

          {/* Billing Details */}
          {showStep(2) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Billing Details</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <FormField
                  control={form.control}
                  name="billingTerms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing Terms</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select terms" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {BILLING_TERMS_OPTIONS.map((opt) => {
                            const o = toOption(opt);
                            return (
                              <SelectItem key={String(o.value)} value={String(o.value)}>
                                {o.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="discount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Discount *</FormLabel>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          {!canEditFinancials && (
                            <TooltipContent>
                              You do not have permission to edit this field
                            </TooltipContent>
                          )}
                          <TooltipTrigger asChild>
                            <FormControl>
                              <div className="flex items-center">
                                <Input
                                  {...field}
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className="rounded-r-none"
                                  disabled={!canEditFinancials}
                                />
                                <span className="flex h-9 items-center rounded-r-md border border-l-0 bg-gray-50 px-2 text-sm text-gray-500">
                                  %
                                </span>
                              </div>
                            </FormControl>
                          </TooltipTrigger>
                        </Tooltip>
                      </TooltipProvider>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="rebate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rebate *</FormLabel>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          {!canEditFinancials && (
                            <TooltipContent>
                              You do not have permission to edit this field
                            </TooltipContent>
                          )}
                          <TooltipTrigger asChild>
                            <FormControl>
                              <div className="flex items-center">
                                <Input
                                  {...field}
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className="rounded-r-none"
                                  disabled={!canEditFinancials}
                                />
                                <span className="flex h-9 items-center rounded-r-md border border-l-0 bg-gray-50 px-2 text-sm text-gray-500">
                                  %
                                </span>
                              </div>
                            </FormControl>
                          </TooltipTrigger>
                        </Tooltip>
                      </TooltipProvider>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="recommendedBy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Referred By</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {showBillingInstructions && (
                <FormField
                  control={form.control}
                  name="billingInstructions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing Instructions</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={3}
                          placeholder="e.g. Send invoice to accounts@company.com by the 1st of each month…"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="employeeDepartmentID"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pricing Model</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PRICING_MODEL_OPTIONS.map((opt) => {
                            const o = toOption(opt);
                            return (
                              <SelectItem key={String(o.value)} value={String(o.value)}>
                                {o.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {isTimeAndMaterials && (
                  <FormField
                    control={form.control}
                    name="customerHourlyRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Hourly Rate
                          <span className="ml-1 text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="flex items-center">
                            <span className="flex h-9 items-center rounded-l-md border border-r-0 bg-gray-50 px-2 text-sm text-gray-500">
                              $
                            </span>
                            <Input
                              {...field}
                              type="number"
                              min={0}
                              step="0.01"
                              className="rounded-l-none"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </section>
          )}

          {/* Account Management */}
          {showStep(2) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Account Management</h3>
              <FormField
                control={form.control}
                name="managedBy"
                render={({ field }) => (
                  <FormItem className="max-w-xs">
                    <FormLabel>Account Manager</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={employeesQuery.isLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={employeesQuery.isLoading ? 'Loading…' : 'Select manager'}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {employeeOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>
          )}

          {/* Notes */}
          {showStep(2) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Notes</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Notes</FormLabel>
                      <p className="text-muted-foreground text-xs">Visible to customer</p>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="intNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Internal Notes</FormLabel>
                      <p className="text-muted-foreground text-xs">NOT visible to customer</p>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>
          )}

          {showStep(2) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Groups</h3>
              {groupsQuery.isLoading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {groups.map((g) => (
                    <div key={g.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`group-${g.id}`}
                        checked={selectedGroupIds.has(g.id)}
                        onCheckedChange={(checked) => toggleGroup(g.id, Boolean(checked))}
                        disabled={isSubmitting}
                      />
                      <Label
                        htmlFor={`group-${g.id}`}
                        className="cursor-pointer text-sm font-normal"
                      >
                        {g.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Property — create only; the customer's first property is provisioned alongside them. */}
          {isCreate && showStep(1) && (
            <section className="space-y-3">
              <h3 className={sectionHeadingClass}>Property</h3>
              <FormField
                control={form.control}
                name="billingSameAsProperty"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(v) => field.onChange(Boolean(v))}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer font-normal">
                      Billing same as property
                    </FormLabel>
                  </FormItem>
                )}
              />
              {!billingSameAsProperty && (
                <>
                  <FormField
                    control={form.control}
                    name="propertyAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Property Address *</FormLabel>
                        <FormControl>
                          <AddressSearchInput
                            value={field.value}
                            onChange={field.onChange}
                            onSelect={(a) => {
                              form.setValue('propertyAddress', a.address, { shouldValidate: true });
                              form.setValue('propertyCity', a.city, { shouldValidate: true });
                              form.setValue('propertyState', a.state, { shouldValidate: true });
                              form.setValue('propertyZip', a.zip, { shouldValidate: true });
                            }}
                            placeholder="Search address..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="propertyCity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City *</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="propertyState"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {USA_STATES_OPTIONS.map((opt) => {
                                const o = toOption(opt);
                                return (
                                  <SelectItem key={String(o.value)} value={String(o.value)}>
                                    {o.label}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="propertyZip"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ZIP Code *</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              )}
              <FormField
                control={form.control}
                name="propertyStoreNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Store/Unit Number (optional)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 1234" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="propertyOwnershipTypeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property Ownership *</FormLabel>
                    <FormControl>
                      <Tabs value={field.value} onValueChange={field.onChange} className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="1">Owner-Occupied</TabsTrigger>
                          <TabsTrigger value="2">Tenant-Occupied</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="propertyNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={3}
                        placeholder="Any special instructions or details..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>
          )}

          {guided ? (
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => (step === 0 ? onCancel() : setStep(step - 1))}
              >
                {step === 0 ? 'Cancel' : 'Back'}
              </Button>
              {step < GUIDED_STEPS.length - 1 ? (
                <Button type="button" onClick={() => void goToNextStep()}>
                  Next
                </Button>
              ) : (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving…' : 'Create Customer'}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                {isSubmitting ? 'Saving…' : isCreate ? 'Create Customer' : 'Save Changes'}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};
